import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@reflex/ui/client/http';
import { useSocketMessage } from '@reflex/ui/client/socket';
import {
  AGENT_LISTENER_WS_EVENT,
  type AgentCapabilityAnswer,
  type AgentListener,
  type ListAgentListenersResponse,
  type WsServerMessage,
} from '@reflex/shared';

const listenersKey = (agentId: string) => ['agent-listeners', agentId] as const;

/**
 * The listeners an agent armed for itself, live: refetches on the
 * `agent_listener:updated` push the server sends on every status change
 * (arm, fire, expiry, decision), which is the one event that changes the
 * list. `enabled` lets the dock skip the fetch until the org capability allows
 * the section.
 */
export function useAgentListeners(agentId: string, opts: { enabled?: boolean } = {}) {
  const qc = useQueryClient();
  useSocketMessage(
    useCallback(
      (msg: WsServerMessage) => {
        if (
          msg.type === 'flow_event' &&
          msg.flowEventType === AGENT_LISTENER_WS_EVENT &&
          (msg.payload as { agentId?: string } | undefined)?.agentId === agentId
        ) {
          qc.invalidateQueries({ queryKey: listenersKey(agentId) });
        }
      },
      [qc, agentId],
    ),
  );
  return useQuery({
    queryKey: listenersKey(agentId),
    queryFn: () =>
      request<ListAgentListenersResponse>(`/agents/${encodeURIComponent(agentId)}/listeners`),
    enabled: opts.enabled ?? true,
    select: (data) => data.listeners,
  });
}

function useListenerMutation<TVars>(agentId: string, run: (vars: TVars) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSettled: () => qc.invalidateQueries({ queryKey: listenersKey(agentId) }),
  });
}

export function useDecideCapabilityRequest(agentId: string) {
  return useListenerMutation(
    agentId,
    ({ requestId, answer }: { requestId: string; answer: AgentCapabilityAnswer }) =>
      request<{ listener?: AgentListener }>(
        `/agents/${encodeURIComponent(agentId)}/capability-requests/${encodeURIComponent(requestId)}/decision`,
        { method: 'POST', body: JSON.stringify({ answer }) },
      ),
  );
}

export function useCancelAgentListener(agentId: string) {
  return useListenerMutation(agentId, ({ listenerId }: { listenerId: string }) =>
    request<{ listener: AgentListener }>(
      `/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/cancel`,
      { method: 'POST', body: '{}' },
    ),
  );
}

export function useRenewAgentListener(agentId: string) {
  return useListenerMutation(
    agentId,
    ({ listenerId, forMs }: { listenerId: string; forMs?: number }) =>
      request<{ listener: AgentListener }>(
        `/agents/${encodeURIComponent(agentId)}/listeners/${encodeURIComponent(listenerId)}/renew`,
        { method: 'POST', body: JSON.stringify(forMs ? { forMs } : {}) },
      ),
  );
}
